// ============================================================
// ExpressSwaggerEnhancements.jsx
//
// A branded, responsive course content page for the
// "Advanced Swagger Enhancements" lesson.
//
// Author: ProDev Architecture Series (Documentation Branch)
// Features: Bearer Auth configuration, reusable Schemas,
//           Tag grouping, and Dashboard navigation.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SecurityIcon           from "@mui/icons-material/Security";
import LoyaltyIcon            from "@mui/icons-material/Loyalty";
import SchemaIcon             from "@mui/icons-material/Schema";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import HubIcon                from "@mui/icons-material/Hub";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

/**
 * SectionTitle Component
 * Renders a stylized icon and heading for content sections.
 */
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

/**
 * CodeBlock Component
 * Standardized code display with a copy-to-clipboard feature.
 */
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressSwaggerEnhancements() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{
            minHeight: "100vh",
            background: BRAND.bg,
            py: { xs: 4, md: 7 },
            px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Advanced Documentation · Module 28"
                        sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }}
                    />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Swagger <span>Optimizations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Take your API documentation to the next level. By implementing Bearer Authentication, Tag grouping, and reusable Schemas, you create a scalable, professional developer portal.
                    </Typography>
                </Box>

                {/* 1. BEARER AUTH */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="1. Implementing Bearer Auth" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Enable the global "Authorize" button in Swagger UI to allow developers to paste their JWTs and test protected routes.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "components: {", comment: "Inside definition object" },
                        { cmd: "  securitySchemes: {", comment: "" },
                        { cmd: "    bearerAuth: {", comment: "" },
                        { cmd: "      type: 'http',", comment: "" },
                        { cmd: "      scheme: 'bearer',", comment: "" },
                        { cmd: "      bearerFormat: 'JWT'", comment: "" },
                        { cmd: "    }", comment: "" },
                        { cmd: "  }", comment: "" },
                        { cmd: "},", comment: "" },
                        { cmd: "security: [{ bearerAuth: [] }]", comment: "Apply globally" },
                    ]} />
                </Box>

                {/* 2. TAG GROUPING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LoyaltyIcon />} label="2. Organising with Tags" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Prevent a "wall of routes" by grouping related endpoints into logical domains like Authentication, Users, or Billing.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "/**", comment: "" },
                        { cmd: " * @swagger", comment: "" },
                        { cmd: " * tags:", comment: "" },
                        { cmd: " * name: Auth", comment: "" },
                        { cmd: " * description: Management of session tokens", comment: "" },
                        { cmd: " *", comment: "" },
                        { cmd: " * /login:", comment: "" },
                        { cmd: " * post:", comment: "" },
                        { cmd: " * tags: [Auth]", comment: "Assign to group" },
                        { cmd: " */", comment: "" },
                    ]} />
                </Box>

                {/* 3. REUSABLE SCHEMAS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SchemaIcon />} label="3. Centralized Schema Definitions" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Define your data models once and reference them everywhere. This keeps your documentation DRY and ensuring consistency.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "components: {", comment: "" },
                        { cmd: "  schemas: {", comment: "" },
                        { cmd: "    User: {", comment: "The Blueprint" },
                        { cmd: "      type: 'object',", comment: "" },
                        { cmd: "      properties: {", comment: "" },
                        { cmd: "        id: { type: 'string' },", comment: "" },
                        { cmd: "        email: { type: 'string' }", comment: "" },
                        { cmd: "      }", comment: "" },
                        { cmd: "    }", comment: "" },
                        { cmd: "  }", comment: "" },
                        { cmd: "}", comment: "" },
                    ]} />
                    <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 1, mt: 3, fontWeight: 700 }}>🔗 Referencing in Routes</Typography>
                    <CodeBlock lines={[
                        { cmd: "responses:", comment: "" },
                        { cmd: "  200:", comment: "" },
                        { cmd: "    content:", comment: "" },
                        { cmd: "      application/json:", comment: "" },
                        { cmd: "        schema:", comment: "" },
                        { cmd: "          $ref: '#/components/schemas/User'", comment: "Dynamic link" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HubIcon />} label="Documentation Strategies" />
                    <Grid container spacing={2}>
                        {[
                            { title: "DRY Schemas", desc: "Always use $ref for objects used in more than one endpoint.", icon: <CheckCircleOutlineIcon /> },
                            { title: "Error Objects", desc: "Document common error responses (401, 403, 404) with their own schemas.", icon: <StarIcon /> },
                            { title: "Domain Tags", desc: "Group by functionality, not file structure, for better DX (Developer Experience).", icon: <LoyaltyIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Swagger UI works best when you provide real examples. Use the <code style={{ color: BRAND.goldBright }}>example</code> field within your schema properties to show developers exactly what the data looks like.
                    </Typography>
                </Box>
            </Container>

            {/* Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
